import React, { useState, useEffect } from "react";
import { Search, ChevronDown, Download } from "lucide-react";
import { notifications } from "@mantine/notifications";
import { TextInput } from "@mantine/core";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { TableLoader } from "../../../components/Loader/LoadingOverlay";

interface Payment {
  id: string;
  reference: string;
  beneficiary: string;
  amount: string;
  status: "Successful" | "Pending" | "Failed";
  date: string;
}

interface PaymentStats {
  total_disbursed: number;
  this_month: number;
  pending: number;
}

interface PaymentsResponse {
  results: Payment[];
  count: number;
}

function StatCard({
  title,
  value,
  hint,
}: {
  title: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="dashboard-stat transition-transform duration-200 hover:-translate-y-1">
      <div className="dashboard-stat__title">{title}</div>
      <div className="dashboard-stat__value">{value}</div>
      <div className="dashboard-stat__hint">{hint}</div>
    </div>
  );
}

export default function Payments() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("Newest");
  const [isLoading, setIsLoading] = useState(true);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState<PaymentStats>({
    total_disbursed: 0,
    this_month: 0,
    pending: 0,
  });

  useEffect(() => {
    fetchPayments();
    fetchPaymentStats();
  }, [searchQuery]);

  async function fetchPayments() {
    setIsLoading(true);
    try {
      const endpoint = Endpoints.GetPayments({
        search: searchQuery || undefined,
      });
      const response = await PerformRequest<PaymentsResponse>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        const data = Array.isArray(response.data)
          ? { results: response.data, count: response.data.length }
          : response.data;
        setPayments(data.results || []);
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to load payments. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "Failed to load payments. Please try again.",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchPaymentStats() {
    try {
      const endpoint = Endpoints.GetPaymentStats();
      const response = await PerformRequest<PaymentStats>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        setStats({
          total_disbursed: response.data.total_disbursed ?? 0,
          this_month: response.data.this_month ?? 0,
          pending: response.data.pending ?? 0,
        });
      }
    } catch (error: any) {
      // Silently fail for stats
    }
  }

  const mockPayouts = [
    {
      ref: "PY-1102",
      beneficiary: "Assigned Counsel",
      amount: "₦180,000",
      status: "Successful",
      date: "Dec 08, 2024",
    },
    {
      ref: "PY-1098",
      beneficiary: "Branch Allocation",
      amount: "₦75,000",
      status: "Successful",
      date: "Dec 06, 2024",
    },
    {
      ref: "PY-1091",
      beneficiary: "Platform Fee",
      amount: "₦20,000",
      status: "Pending",
      date: "Dec 03, 2024",
    },
    {
      ref: "PY-1085",
      beneficiary: "Assigned Counsel",
      amount: "₦150,000",
      status: "Successful",
      date: "Nov 28, 2024",
    },
    {
      ref: "PY-1079",
      beneficiary: "Branch Allocation",
      amount: "₦60,000",
      status: "Successful",
      date: "Nov 25, 2024",
    },
  ];

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__header">
        <div className="dashboard-page__header-left">
          <h1 className="dashboard-page__title">Payments</h1>
          <p className="dashboard-page__subtitle">
            Track disbursements and allocations.
          </p>
        </div>
      </div>

      <div className="dashboard-stats dashboard-stats--three">
        <StatCard
          title="Total Disbursed"
          value={`₦${((stats.total_disbursed ?? 0) / 1000000).toFixed(1)}M`}
          hint="All time"
        />
        <StatCard
          title="This Month"
          value={`₦${((stats.this_month ?? 0) / 1000000).toFixed(1)}M`}
          hint="Dec 2024"
        />
        <StatCard
          title="Pending"
          value={`₦${(stats.pending ?? 0).toLocaleString()}`}
          hint="Requires action"
        />
      </div>

      <div className="dashboard-filters">
        <TextInput
          placeholder="Search payments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          leftSection={<Search size={18} />}
          style={{ flex: 1 }}
        />

        <div className="dashboard-sort">
          <span className="dashboard-sort__label">{sortBy}</span>
          <button
            className="dashboard-sort__button"
            type="button"
            onClick={() => setSortBy(sortBy === "Newest" ? "Oldest" : "Newest")}
          >
            <ChevronDown size={16} />
          </button>
        </div>

        <button
          className="dashboard-button dashboard-button--secondary"
          type="button"
        >
          <Download size={16} />
          <span>Export</span>
        </button>
      </div>

      <section className="dashboard-panel shadow-[var(--card-shadow)] rounded-2xl">
        <div className="dashboard-panel__header">
          <h2 className="dashboard-panel__title">All Payouts</h2>
        </div>
        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Beneficiary</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableLoader colSpan={5} />
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8">
                    No payments found
                  </td>
                </tr>
              ) : (
                payments.map((p) => (
                  <tr
                    key={p.reference || p.id}
                    className="transition-colors duration-200 hover:bg-black/5"
                  >
                    <td>{p.reference}</td>
                    <td>{p.beneficiary}</td>
                    <td className="dashboard-table__amount">{p.amount}</td>
                    <td>
                      <span
                        className={
                          p.status === "Successful"
                            ? "badge badge--success"
                            : "badge badge--pending"
                        }
                      >
                        {p.status}
                      </span>
                    </td>
                    <td>{p.date}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-pagination">
          <div className="dashboard-pagination__info">
            <span>Page</span>
            <span className="dashboard-pagination__current">1</span>
            <span>of 3</span>
          </div>
          <div className="dashboard-pagination__controls">
            <button
              className="dashboard-pagination__button"
              type="button"
              disabled
              aria-label="Previous page"
            >
              <ChevronDown size={16} className="rotate-90" />
            </button>
            <button
              className="dashboard-pagination__button"
              type="button"
              aria-label="Next page"
            >
              <ChevronDown size={16} className="-rotate-90" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
